import React, { useEffect, useMemo, useState } from 'react';
import TabNav from '../../composants/TabNav/TabNav';
import { LotCard, getLotFamille, LotTechnique } from '../../composants/LotCard/LotCard';
import CatalogueLotModal from './CatalogueLotModal';
import LancerMarcheModal from '../../composants/LancerMarcheModal/LancerMarcheModal';
import Button from '../../composants/Button/Button';
import LibelleNouveauMarche from '../../composants/LibelleNouveauMarche/LibelleNouveauMarche';
import { ReactComponent as PlusIcon } from '../../assets/icons/plus.svg';
import { apiService } from '../../services/apiService';
// Grille STRICTEMENT identique a l'accueil (.accueil-lots-mosaic / .accueil-lot-card* + skeleton),
// scope .accueil-* (aucune fuite globale).
import '../Accueil/Accueil.css';
import './Catalogue.css';

// Onglets = familles de lots (par 1er chiffre du code, cf. getLotFamille). Index 0 = "Tous".
const FAMILLES = ['Tous', 'Ingénierie', 'Travaux', 'Services'];
const LOTS_SKELETON_COUNT = 25;

interface LotDepartement {
  id: number;
  id_departement: number;
  departement?: { id: number; nom: string };
}

interface Option {
  value: string;
  label: string;
}

const normalize = (s: string) => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

const Catalogue: React.FC = () => {
  const [lots, setLots] = useState<LotTechnique[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [geoOptions, setGeoOptions] = useState<Option[]>([]);

  // Filtres. `geo` est le SEUL controle du lot geographique (la modale n'en a plus) : il determine le
  // titulaire dont on affiche les articles et les prix. Il est pre-selectionne sur le departement du
  // siege social de l'organisme, et reste modifiable.
  const [activeTab, setActiveTab] = useState(0);
  const [geo, setGeo] = useState('');
  const [tech, setTech] = useState('');
  const [search, setSearch] = useState('');

  // Pre-selection du lot geographique sur le siege social. On ne l'applique qu'une fois, et jamais
  // par-dessus un choix de l'acheteur (il peut avoir change le filtre avant la fin de l'appel).
  useEffect(() => {
    let annule = false;
    apiService
      .getSiegeDepartement()
      .then((siege) => {
        if (annule || !siege) return;
        setGeo((actuel) => (actuel === '' ? String(siege.id_departement) : actuel));
      })
      .catch(() => undefined);
    return () => {
      annule = true;
    };
  }, []);

  // Bon de commande : lot clique -> ouvre LancerMarcheModal (BC) pre-remplie (lot + geo). La modale
  // resout le titulaire (lot x geo), cree le brouillon et redirige vers le suivi ou l'acheteur choisit
  // les articles (prix reels du titulaire) ou laisse le prestataire remplir.
  const [bcLot, setBcLot] = useState<LotTechnique | null>(null);

  // Boutons "Nouveau Bon de commande" / "Nouveau Marche subsequent" (rangee de filtres) : ouvrent
  // LancerMarcheModal (memes modales que la page Commandes), pre-remplie avec le lot technique + le
  // departement filtres si l'acheteur en a choisi.
  const [nouveauMode, setNouveauMode] = useState<'BC' | 'MS' | null>(null);

  // Lots (grille) — meme source que l'accueil : GET public /lots-technique.
  useEffect(() => {
    const run = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/lots-technique`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        setLots(json.data ?? []);
      } catch {
        // Silencieux : la mosaique reste vide.
      }
    };
    run().finally(() => setLoaded(true));
  }, []);

  // Lots geographiques (departements) pour le select geo : GET public /lots-departements.
  useEffect(() => {
    const run = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/lots-departements`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        const list: LotDepartement[] = Array.isArray(json) ? json : (json.data ?? []);
        // Lot geographique = departement. "Code" = id_departement (convention app / select Annuaire) ->
        // libelle "code - nom" (ex. "1 - Ain", "75 - Paris"), trie par code numerique.
        const seen = new Map<string, string>();
        list.forEach((ld) => {
          const id = ld.departement?.id ?? ld.id_departement;
          const nom = ld.departement?.nom;
          if (id != null && nom) seen.set(String(id), nom);
        });
        setGeoOptions(
          Array.from(seen.entries())
            .map(([value, nom]) => ({ value, label: `${value} - ${nom}` }))
            .sort((a, b) => Number(a.value) - Number(b.value)),
        );
      } catch {
        // Silencieux : le select geo reste vide.
      }
    };
    run();
  }, []);

  const sortedLots = useMemo(
    () => [...lots].sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true })),
    [lots],
  );

  const techOptions = useMemo<Option[]>(
    () => sortedLots.map((l) => ({ value: l.code, label: `${l.code} - ${l.nom}` })),
    [sortedLots],
  );

  // Le filtre `tech` porte le CODE du lot ; LancerMarcheModal attend l'id -> on le resout via sortedLots.
  const techLotId = useMemo(
    () => (tech ? sortedLots.find((l) => l.code === tech)?.id : undefined),
    [tech, sortedLots],
  );

  const visibleLots = useMemo(() => {
    const famille = activeTab === 0 ? null : FAMILLES[activeTab];
    const q = normalize(search.trim());
    return sortedLots.filter((lot) => {
      if (famille && getLotFamille(lot.code).label !== famille) return false;
      if (tech && lot.code !== tech) return false;
      // Recherche au niveau lot (code + nom). La recherche DANS les prestations (articles des
      // catalogues) et le filtrage geographique restent a cabler (catalogue national a ce stade).
      if (q && !normalize(`${lot.code} ${lot.nom}`).includes(q)) return false;
      return true;
    });
  }, [sortedLots, activeTab, tech, search]);

  return (
    <div className="catalogue-page">
      <section className="tableau-de-bord-section">
        <TabNav tabs={FAMILLES} activeTab={activeTab} onTabChange={setActiveTab} />

        {/* Filtres calques sur Annuaire : rangee flex de blocs (label au-dessus d'un champ natif). */}
        <div className="catalogue-filters">
          <div className="catalogue-filter">
            <label htmlFor="catalogue-geo">Lot géographique</label>
            <select id="catalogue-geo" value={geo} onChange={(e) => setGeo(e.target.value)}>
              <option value="">Tous les départements</option>
              {geoOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div className="catalogue-filter">
            <label htmlFor="catalogue-tech">Lot technique</label>
            <select id="catalogue-tech" value={tech} onChange={(e) => setTech(e.target.value)}>
              <option value="">Tous les lots techniques</option>
              {techOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div className="catalogue-filter">
            <label htmlFor="catalogue-search">Prestation</label>
            <input
              id="catalogue-search"
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Trouver une prestation"
            />
          </div>

          {/* Actions de creation, alignees dans la rangee de filtres (5 colonnes). Memes modales que la
              page Commandes (LancerMarcheModal, mode BC / MS). */}
          <div className="catalogue-filter catalogue-filter--action">
            <Button
              value={<LibelleNouveauMarche forme="BC" />}
              variant="primary"
              size="small"
              icon={<PlusIcon />}
              onClick={() => setNouveauMode('BC')}
            />
          </div>
          <div className="catalogue-filter catalogue-filter--action">
            <Button
              value={<LibelleNouveauMarche forme="MS" />}
              variant="primary"
              size="small"
              icon={<PlusIcon />}
              onClick={() => setNouveauMode('MS')}
            />
          </div>
        </div>

        <div className="accueil-lots-mosaic">
          {loaded
            ? visibleLots.map((lot) => (
                <LotCard key={lot.id} lot={lot} onClick={() => setBcLot(lot)} />
              ))
            : Array.from({ length: LOTS_SKELETON_COUNT }).map((_, i) => (
                <div
                  key={`lot-skeleton-${i}`}
                  className="accueil-lot-card accueil-lot-card--skeleton"
                  aria-hidden="true"
                >
                  <div className="accueil-lot-card-img-wrap accueil-skeleton-shimmer" />
                  <div className="accueil-lot-card-body">
                    <span className="accueil-skeleton-line accueil-skeleton-line--code" />
                    <span className="accueil-skeleton-line accueil-skeleton-line--nom" />
                  </div>
                </div>
              ))}
        </div>

        {loaded && visibleLots.length === 0 && (
          <p className="catalogue-empty">Aucun lot ne correspond à ces filtres.</p>
        )}
      </section>

      {/* Clic sur un lot -> modale de composition d'un bon de commande. Le lot geographique est pre-rempli
          depuis le filtre de la page (sinon la modale le demande) ; il debloque le titulaire + ses prix.
          Le brouillon n'est cree qu'a la validation, puis redirection vers le suivi (/operation-bc/{id}). */}
      {bcLot && (
        <CatalogueLotModal
          lot={bcLot}
          geo={geo}
          geoLabel={geoOptions.find((o) => o.value === geo)?.label}
          onClose={() => setBcLot(null)}
        />
      )}

      {/* Creation directe depuis la barre de filtres (hors clic sur un lot). Lot technique + departement
          pre-remplis depuis les filtres de la page si l'acheteur en a choisi. */}
      {nouveauMode && (
        <LancerMarcheModal
          isOpen
          onClose={() => setNouveauMode(null)}
          title={nouveauMode === 'BC' ? 'Nouveau Bon de Commande' : 'Lancer un marché subséquent'}
          showNomOperation
          showLotTechnique
          showLotGeographique
          showDepartement={false}
          mode={nouveauMode}
          initialValues={{ lotTechnique: techLotId != null ? String(techLotId) : '', lotGeographique: geo }}
        />
      )}
    </div>
  );
};

export default Catalogue;
